import React, { useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import emailjs from '@emailjs/browser';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ContactContainer = styled.div`
  min-height: 100vh;
  padding: 5rem 2rem 4rem;
  background: var(--bg-secondary);
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  animation: ${fadeIn} 0.8s ease;
`;

const PageTitle = styled.h1`
  font-size: clamp(3rem, 6vw, 5rem);
  color: var(--text-primary);
  margin-bottom: 1rem;
  font-weight: 900;
  font-family: 'Space Grotesk', sans-serif;

  span { color: #F5B800; }
`;

const PageSubtitle = styled.p`
  font-size: clamp(1.1rem, 2vw, 1.4rem);
  color: var(--text-secondary);
  max-width: 700px;
  margin: 0 auto;
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 3rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div`
  background: linear-gradient(135deg, #0E1114 0%, #2C2F33 100%);
  padding: 3.5rem 3rem;
  border-radius: 4px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  border-top: 4px solid #F5B800;
  color: white;
`;

const InfoTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 2.8rem);
  color: #F5B800;
  margin-bottom: 3rem;
  font-weight: 800;
  font-family: 'Space Grotesk', sans-serif;
`;

const InfoItem = styled.div`
  margin-bottom: 2rem;
  padding: 1.75rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  border-left: 4px solid #F5B800;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateX(10px);
  }
`;

const InfoLabel = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-weight: 600;
`;

const InfoValue = styled.div`
  font-size: 1.3rem;
  color: white;
  font-weight: 600;

  a {
    color: #F5B800;
    transition: all 0.3s ease;

    &:hover {
      color: #2EC4B6;
    }
  }
`;

const CompanyInfo = styled.div`
  margin-top: 2.5rem;
  padding: 1.75rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 4px;
  border: 1px solid rgba(245, 184, 0, 0.3);
`;

const CompanyName = styled.h3`
  color: #F5B800;
  margin-bottom: 0.75rem;
  font-size: 1.3rem;
  font-weight: 700;
  font-family: 'Space Grotesk', sans-serif;
`;

const CompanyDetails = styled.p`
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.8;
  font-size: 1rem;
`;

const FormSection = styled.div`
  background: var(--card-bg);
  padding: 3.5rem 3rem;
  border-radius: 4px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #F5B800;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const FormTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 2.8rem);
  color: var(--text-primary);
  margin-bottom: 2rem;
  font-weight: 800;
  font-family: 'Space Grotesk', sans-serif;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 1rem;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const Input = styled.input`
  padding: 1.25rem;
  font-size: 1.05rem;
  border: 2px solid rgba(44, 47, 51, 0.2);
  border-radius: 4px;
  transition: all 0.3s ease;
  font-family: inherit;
  background: var(--bg-secondary);

  &:focus {
    outline: none;
    border-color: #F5B800;
    background: var(--card-bg);
  }
`;

const TextArea = styled.textarea`
  padding: 1.25rem;
  font-size: 1.05rem;
  border: 2px solid rgba(44, 47, 51, 0.2);
  border-radius: 4px;
  min-height: 180px;
  resize: vertical;
  transition: all 0.3s ease;
  font-family: inherit;
  background: var(--bg-secondary);

  &:focus {
    outline: none;
    border-color: #F5B800;
    background: var(--card-bg);
  }
`;

const SubmitButton = styled.button`
  background: #F5B800;
  color: var(--text-primary);
  padding: 1.5rem 3rem;
  font-size: 1.2rem;
  font-weight: 800;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(245, 184, 0, 0.3);
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: 'Space Grotesk', sans-serif;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(245, 184, 0, 0.5);
    background: #2EC4B6;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const Message = styled.div`
  padding: 1.5rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  background: ${props => props.type === 'success' ?
    'rgba(46, 196, 182, 0.1)' : 'rgba(214, 40, 40, 0.1)'};
  color: ${props => props.type === 'success' ? '#0077B6' : '#D62828'};
  border-left: 4px solid ${props => props.type === 'success' ? '#2EC4B6' : '#D62828'};
  font-weight: 600;
`;

/**
 * Contact form component with EmailJS integration.
 * EmailJS credentials are stored in environment variables for security.
 */
const Contact = () => {
  const formRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // EmailJS Configuration - Set these in your .env file
  const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
  const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  /**
   * Handles form submission using EmailJS.
   * Sends email directly without opening email client.
   * @param {Event} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    // Check if EmailJS is configured
    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      // Fallback to mailto if EmailJS not configured
      const subject = encodeURIComponent(formData.subject || 'Inquiry from Vital-Link Website');
      const body = encodeURIComponent(
        `Name: ${formData.name}\n` +
        `Email: ${formData.email}\n` +
        `Phone: ${formData.phone}\n\n` +
        `Message:\n${formData.message}`
      );
      window.location.href = `mailto:support@vital-link-cable-solutions.com?subject=${subject}&body=${body}`;
      setStatus({
        type: 'success',
        message: 'Opening your email client...'
      });
      setIsSubmitting(false);
      return;
    }

    try {
      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        formRef.current,
        EMAILJS_PUBLIC_KEY
      );

      setStatus({
        type: 'success',
        message: 'Thank you! Your message has been sent successfully. We\'ll get back to you soon!'
      });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('EmailJS Error:', error);
      setStatus({
        type: 'error',
        message: 'Sorry, there was an error sending your message. Please try again or email us directly at support@vital-link-cable-solutions.com'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactContainer>
      <Container>
        <PageHeader>
          <PageTitle>Contact <span>Us</span></PageTitle>
          <PageSubtitle>Get in Touch with Vital-Link</PageSubtitle>
        </PageHeader>

        <ContentWrapper>
          <ContactInfo>
            <InfoTitle>Contact Information</InfoTitle>

            <InfoItem>
              <InfoLabel>Phone</InfoLabel>
              <InfoValue>
                <a href="tel:850-730-0401">850-730-0401</a>
              </InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Email</InfoLabel>
              <InfoValue>
                <a href="mailto:support@vital-link-cable-solutions.com">support@vital-link-cable-solutions.com</a>
              </InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Service Area</InfoLabel>
              <InfoValue>
                United States - Nationwide Coverage
              </InfoValue>
            </InfoItem>

            <CompanyInfo>
              <CompanyName>Vital-Link Structured Wiring and Advanced Engineering LLC</CompanyName>
              <CompanyDetails>
                Delaware State File Number: 10462141<br />
                Date of Formation: January 6, 2026
              </CompanyDetails>
            </CompanyInfo>
          </ContactInfo>

          <FormSection>
            <FormTitle>Send Us a Message</FormTitle>
            {status.message && (
              <Message type={status.type}>{status.message}</Message>
            )}
            <Form ref={formRef} onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">Name *</Label>
                <Input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">Email *</Label>
                <Input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="phone">Phone</Label>
                <Input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="subject">Subject *</Label>
                <Input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange} required />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="message">Message *</Label>
                <TextArea id="message" name="message" value={formData.message} onChange={handleChange} required />
              </FormGroup>

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </SubmitButton>
            </Form>
          </FormSection>
        </ContentWrapper>
      </Container>
    </ContactContainer>
  );
};

export default Contact;
